import React, { useState, useRef } from "react";
import { toast } from "react-hot-toast";
import { useHistory, useLocation } from "react-router-dom";
import { HiChevronDown, HiChevronUp, HiInformationCircle, HiLink, HiOutlinePlus, HiOutlineX } from "react-icons/hi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import GroupIdLookup from "../../components/GroupIdLookup";
import Trigger from "../../components/question/Trigger";
import DependencyExplorer from "../../components/dependencies/DependencyExplorer";
import { capture } from "../../instrument";
import { validateTriggerGroups, INPUT_CLASS_NAME, guidelineText } from "../../utils";

const AUDIENCES = [
  { audience: "secretary", label: "Secrétaire", dotClass: "bg-purple-500" },
  { audience: "patient", label: "Patient", dotClass: "bg-blue-500" },
  { audience: "ide", label: "IDE", dotClass: "bg-green-500" },
];

const GUIDELINE_TEXTAREA_CLASS = "w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-relaxed text-gray-900 placeholder:text-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

const AddGuideline = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const duplicate = location.state?.duplicate;
  const [secretary, setSecretary] = useState(duplicate?.secretary ?? false);
  const [secretaryGuideline, setSecretaryGuideline] = useState(duplicate?.secretaryGuideline || "");
  const [patient, setPatient] = useState(duplicate?.patient ?? false);
  const [patientGuideline, setPatientGuideline] = useState(duplicate?.patientGuideline || "");
  const [ide, setIde] = useState(duplicate?.ide ?? false);
  const [ideGuideline, setIdeGuideline] = useState(duplicate?.ideGuideline || "");
  const [canShowInPreRegistration, setCanShowInPreRegistration] = useState(duplicate?.canShowInPreRegistration || false);
  const [activeAudience, setActiveAudience] = useState("secretary");
  const [groupsOperator, setGroupsOperator] = useState(duplicate?.groupsOperator || "AND");
  const [triggerGroups, setTriggerGroups] = useState(duplicate?.triggerGroups || [{ operator: "AND", triggers: [{ type: "CODE_SOME", values: [], field: "" }] }]);
  const [groupId, setGroupId] = useState(duplicate?.groupId || "");
  const [priority, setPriority] = useState(duplicate?.priority ?? 0);
  const [ubilabUrl, setUbilabUrl] = useState(duplicate?.ubilabUrl || "");
  const [showAdvanced, setShowAdvanced] = useState(duplicate ? !!duplicate.groupId || duplicate.priority > 0 : false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const textareaRefs = useRef({});
  const diagramOpen = true;

  const { data: relatedGuidelines = [] } = useQuery({
    queryKey: ["guidelines", "byGroup", groupId],
    queryFn: async () => {
      const response = await API.get(`/guideline?groupId=${groupId}`);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    enabled: !!groupId,
  });

  const { data: diagramData } = useQuery({
    queryKey: ["diagramData"],
    queryFn: async () => {
      const [questions, guidelines, actions] = await Promise.all([API.get("/question"), API.get("/guideline"), API.get("/action")]);
      if (!questions.ok || !guidelines.ok || !actions.ok) throw new Error("Failed to load diagram data");
      return { questions: questions.data, guidelines: guidelines.data, actions: actions.data };
    },
    enabled: diagramOpen,
  });

  const enabledAudiences = AUDIENCES.filter(({ audience }) => (audience === "secretary" ? secretary : audience === "patient" ? patient : ide));

  const audienceText = (audience) => (audience === "secretary" ? secretaryGuideline : audience === "patient" ? patientGuideline : ideGuideline);
  const setAudienceText = (audience, value) => {
    if (audience === "secretary") setSecretaryGuideline(value);
    else if (audience === "patient") setPatientGuideline(value);
    else setIdeGuideline(value);
  };
  const setAudienceEnabled = (audience, enabled) => {
    if (audience === "secretary") setSecretary(enabled);
    else if (audience === "patient") setPatient(enabled);
    else setIde(enabled);
  };

  const onAudienceAdd = (audience) => {
    setAudienceEnabled(audience, true);
    if (enabledAudiences.length > 0) setAudienceText(audience, audienceText(enabledAudiences[0].audience));
    setActiveAudience(audience);
  };

  const onAudienceRemove = (audience) => {
    setAudienceEnabled(audience, false);
    setAudienceText(audience, "");
    if (audience === "patient") setCanShowInPreRegistration(false);
  };

  const insertLinkAtCursor = (text, url) => {
    const textarea = textareaRefs.current[activeAudience];
    const markdownLink = `[${text}](${url})`;
    if (!textarea) {
      setAudienceText(activeAudience, audienceText(activeAudience) + markdownLink);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = audienceText(activeAudience);
    const newGuidelineText = currentText.substring(0, start) + markdownLink + currentText.substring(end);
    setAudienceText(activeAudience, newGuidelineText);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + markdownLink.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleInsertLink = () => {
    if (!linkText.trim() || !linkUrl.trim()) {
      toast.error("Veuillez remplir le texte et l'URL du lien");
      return;
    }

    insertLinkAtCursor(linkText.trim(), linkUrl.trim());
    setShowLinkModal(false);
    setLinkText("");
    setLinkUrl("");
  };

  const createMutation = useMutation({
    mutationFn: (body) => API.post("/guideline", body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Recommandation ajoutée avec succès");
      queryClient.invalidateQueries({ queryKey: ["guidelines"] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      history.push("/guideline");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const addGuideline = () => {
    if (!enabledAudiences.length) return toast.error("Veuillez ajouter au moins une audience");
    if (enabledAudiences.some(({ audience }) => !audienceText(audience).trim())) return toast.error("Veuillez remplir tous les champs obligatoires");
    if (!triggerGroups?.length) return toast.error("Veuillez ajouter au moins un groupe de conditions");

    const error = validateTriggerGroups(triggerGroups);
    if (error) return toast.error(error);

    createMutation.mutate({
      secretary,
      secretaryGuideline,
      patient,
      patientGuideline,
      ide,
      ideGuideline,
      canShowInPreRegistration,
      groupsOperator,
      triggerGroups,
      groupId: groupId.trim() || null,
      priority: Number(priority) || 0,
      ubilabUrl: ubilabUrl.trim() || null,
    });
  };

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900">{duplicate ? "Dupliquer une recommandation" : "Ajouter une recommandation"}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            onClick={() => history.push("/guideline")}
          >
            Annuler
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            onClick={addGuideline}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Chargement..." : "Ajouter"}
          </button>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-5 overflow-hidden">
        <div className="col-span-3 overflow-y-auto">
          <div className="mx-auto max-w-4xl px-8 py-6">
            <h3 className="mb-4 text-lg font-medium text-gray-900">Général</h3>

            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
              <div className="flex items-center gap-4 px-5 py-3.5">
                <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">URL Ubilab</label>
                <input type="url" className={INPUT_CLASS_NAME + " flex-1"} placeholder="https://..." value={ubilabUrl} onChange={(e) => setUbilabUrl(e.target.value)} />
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <div className="flex items-baseline justify-between px-1">
                <h4 className="text-sm font-medium text-gray-700">Versions par audience</h4>
                <p className="text-xs text-gray-400">Markdown supporté · liens via [texte](url)</p>
              </div>

              {enabledAudiences.map(({ audience, label, dotClass }) => {
                return (
                  <div key={audience} className="overflow-hidden rounded-lg border border-gray-100 bg-white">
                    <div className="flex items-center gap-2.5 border-b border-gray-100 bg-gray-50/40 px-5 py-2.5">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
                      <span className="text-[13px] font-medium text-gray-800">{label}</span>
                      {audience === "patient" && (
                        <label className="ml-2 flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-1 focus:ring-primary-500"
                            checked={canShowInPreRegistration}
                            onChange={(e) => setCanShowInPreRegistration(e.target.checked)}
                          />
                          Affichable en pré-enregistrement
                        </label>
                      )}
                      <div className="ml-auto flex items-center gap-1">
                        <button
                          type="button"
                          className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-primary-50 hover:text-primary-700"
                          onClick={() => {
                            setActiveAudience(audience);
                            setShowLinkModal(true);
                          }}
                          title="Insérer un lien Markdown"
                        >
                          <HiLink className="h-3.5 w-3.5" />
                          Lien
                        </button>
                        <button
                          type="button"
                          onClick={() => onAudienceRemove(audience)}
                          className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                          title={`Supprimer la version ${label}`}
                        >
                          <HiOutlineX className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="px-5 py-4">
                      <textarea
                        ref={(el) => { textareaRefs.current[audience] = el; }}
                        className={GUIDELINE_TEXTAREA_CLASS}
                        rows={5}
                        placeholder="Saisissez votre recommandation ici…"
                        value={audienceText(audience)}
                        onChange={(e) => setAudienceText(audience, e.target.value)}
                        onFocus={() => setActiveAudience(audience)}
                      />
                    </div>
                  </div>
                );
              })}

              {enabledAudiences.length < AUDIENCES.length && (
                <div className="flex flex-wrap items-center gap-2 px-1 pt-1">
                  <span className="text-xs text-gray-400">Ajouter une version :</span>
                  {AUDIENCES.filter((candidate) => !enabledAudiences.includes(candidate)).map(({ audience, label, dotClass }) => (
                    <button
                      key={audience}
                      type="button"
                      onClick={() => onAudienceAdd(audience)}
                      className="flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
                      <HiOutlinePlus className="h-3.5 w-3.5" />
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <h3 className="mb-4 mt-8 text-lg font-medium text-gray-900">Conditions</h3>

            <div className="rounded-lg border border-gray-100 bg-white p-4">
              <Trigger groupsOperator={groupsOperator} setGroupsOperator={setGroupsOperator} triggerGroups={triggerGroups} setTriggerGroups={setTriggerGroups} codesIncludeRelated />
            </div>

            <div className="mt-6 border-t border-gray-100 pt-4">
              <button type="button" className="flex items-center text-sm font-medium text-primary-600 hover:text-primary-800" onClick={() => setShowAdvanced(!showAdvanced)}>
                {showAdvanced ? (
                  <>
                    <HiChevronUp className="mr-1" />
                    Masquer les options avancées
                  </>
                ) : (
                  <>
                    <HiChevronDown className="mr-1" />
                    Afficher les options avancées
                  </>
                )}
              </button>

              {showAdvanced && (
                <div className="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
                  <div className="flex items-start gap-4 px-5 py-3.5">
                    <HiInformationCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary-500" />
                    <p className="text-xs text-gray-600">Ces options permettent de gérer des recommandations similaires qui doivent être affichées selon une priorité.</p>
                  </div>
                  <div className="flex items-center gap-4 px-5 py-3.5">
                    <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Groupe d'alternatives</label>
                    <div className="flex-1" data-track="guideline-group-alternatives">
                      <GroupIdLookup onChange={setGroupId} initialValue={groupId} placeholder="Identifiant du groupe (ex: bilan_sanguin)" currentId={null} />
                    </div>
                  </div>
                  {groupId && (
                    <div className="flex items-center gap-4 px-5 py-3.5">
                      <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Priorité</label>
                      <input type="number" className={INPUT_CLASS_NAME} value={priority} onChange={(e) => setPriority(Number(e.target.value) >= 0 ? Number(e.target.value) : 0)} />
                    </div>
                  )}

                  {groupId && relatedGuidelines.length > 0 && (
                    <div className="px-5 py-3.5">
                      <div className="rounded-lg border border-primary-100 bg-primary-50 p-2">
                        <div className="mb-1 text-xs text-primary-800">
                          {relatedGuidelines.length} recommandation{relatedGuidelines.length > 1 ? "s" : ""} dans ce groupe
                        </div>
                        <div className="max-h-32 space-y-1 overflow-y-auto">
                          {relatedGuidelines.map((g) => (
                            <div
                              key={g._id}
                              className="flex cursor-pointer items-center justify-between rounded border border-gray-200 bg-white p-1.5 text-xs hover:bg-primary-50"
                              onClick={() => window.open(`/guideline/${g._id}/edit`, "_blank")}
                            >
                              <div className="mr-2 flex-1 truncate">{guidelineText(g)}</div>
                              <div className="text-gray-500">{g.priority}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-2 shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-6">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Dépendances</h3>
          {diagramData ? (
            <DependencyExplorer
              data={{
                questions: diagramData.questions,
                guidelines: [
                  ...diagramData.guidelines,
                  { _id: "new_guideline", secretary, secretaryGuideline, patient, patientGuideline, ide, ideGuideline, canShowInPreRegistration, groupsOperator, triggerGroups, groupId, priority },
                ],
                actions: diagramData.actions,
              }}
              currentGuidelineId="new_guideline"
              className="rounded-lg border border-gray-100"
            />
          ) : (
            <div className="flex items-center justify-center rounded-lg border border-gray-100 py-8 text-sm text-gray-400">Chargement du diagramme...</div>
          )}
        </div>
      </div>

      {showLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowLinkModal(false)}>
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-4 text-lg font-semibold text-gray-800">Insérer un lien</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Texte du lien</label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 p-2 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                  placeholder="Ex: En savoir plus"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleInsertLink();
                    }
                  }}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">URL</label>
                <input
                  type="url"
                  className="w-full rounded-lg border border-gray-300 p-2 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                  placeholder="https://exemple.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleInsertLink();
                    }
                  }}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                className="rounded-lg bg-gray-200 px-4 py-2 text-gray-800 transition-colors hover:bg-gray-300"
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkText("");
                  setLinkUrl("");
                }}
              >
                Annuler
              </button>
              <button type="button" className="rounded-lg bg-primary-500 px-4 py-2 text-white transition-colors hover:bg-primary-700" onClick={handleInsertLink}>
                Insérer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddGuideline;
